import { inject, Injectable } from '@angular/core';
import { HttpDataAccess, RequestEvent, ResponseRequestEvent } from '@softline/core';
import { filter, lastValueFrom, Observable } from 'rxjs';
import {
  BELEGLESER_API_JOB,
  BELEGLESER_API_JOB_BOX_TEXT,
  BELEGLESER_API_JOB_FINAL_RESULT,
  BELEGLESER_API_JOB_PAGE,
  BELEGLESER_API_JOB_PAGES,
  BELEGLESER_API_JOB_WORDS,
  BELEGLESER_API_JOBS,
} from '../belegleser.api';
import { SOFTLINE_CONFIG_BELEGLESER } from '../belegleser.shared';
import { Job, JobCreated, JobPages, JobWords } from '../types/job';
import { ResultData } from '../types/result';
import { BoxText, BoxTexts, FinalResultReport, PageBox } from '../types/service';
import { belegleserLocation } from './belegleser-location';

/**
 * Job endpoints of the belegleser service. Built on HttpDataAccess (raw URLs), not
 * ApiDataAccess — the service is not the Softline connection.
 */
@Injectable()
export class JobRepository {
  private readonly dataAccess = inject(HttpDataAccess);
  private readonly config = inject(SOFTLINE_CONFIG_BELEGLESER);

  upload(file: File): Observable<RequestEvent<JobCreated>> {
    return this.dataAccess.upload(belegleserLocation(this.config, BELEGLESER_API_JOBS), {
      file,
    });
  }

  get(jobId: string): Promise<Job> {
    return this.dataAccess.get(
      belegleserLocation(this.config, BELEGLESER_API_JOB, { jobId })
    );
  }

  delete(jobId: string): Promise<void> {
    return this.dataAccess.delete(
      belegleserLocation(this.config, BELEGLESER_API_JOB, { jobId })
    );
  }

  pages(jobId: string): Promise<JobPages> {
    return this.dataAccess.get(
      belegleserLocation(this.config, BELEGLESER_API_JOB_PAGES, { jobId })
    );
  }

  words(jobId: string): Promise<JobWords> {
    return this.dataAccess.get(
      belegleserLocation(this.config, BELEGLESER_API_JOB_WORDS, { jobId })
    );
  }

  /** One entry per box, in request order. */
  async boxText(jobId: string, boxes: readonly PageBox[]): Promise<BoxText[]> {
    const response = await this.dataAccess.create<
      { boxes: readonly PageBox[] },
      BoxTexts
    >(belegleserLocation(this.config, BELEGLESER_API_JOB_BOX_TEXT, { jobId }), { boxes });
    return response.boxes;
  }

  /** Answered in the shape of `result`; the service diffs it against the engine result. */
  reportFinalResult(jobId: string, result: ResultData): Promise<FinalResultReport> {
    return this.dataAccess.update(
      belegleserLocation(this.config, BELEGLESER_API_JOB_FINAL_RESULT, { jobId }),
      result
    );
  }

  /**
   * PNG of one page, through HttpClient so the bearer token is attached. The rotation
   * doubles as cache buster — the service ignores query parameters.
   */
  async pageImage(jobId: string, page: number, rotation = 0): Promise<Blob> {
    const location = belegleserLocation(
      this.config,
      BELEGLESER_API_JOB_PAGE,
      { jobId, page },
      { v: rotation }
    );
    const event = await lastValueFrom(
      this.dataAccess
        .download(location)
        .pipe(
          filter(
            (e): e is ResponseRequestEvent<Blob | ArrayBuffer> => e?.type === 'response'
          )
        )
    );
    if (!(event.response instanceof Blob))
      throw new Error(`page ${page} of job ${jobId} answered without an image`);
    return event.response;
  }
}
